import { PurchaseOrderDetails } from 'src/WebApiController';

export interface AdjustFxRateRequest {
  purchaseOrderId: number;
  newFxRate: number;
}

export interface AdjustFxRatePreviewResponse {
  purchaseOrderId: number;
  originalCurrency: string;
  targetCurrency: string;
  currentFxRate: number;
  newFxRate: number;
  currentPoCosts: {
    totalCost: number;
    unitCost: number;
  };
  previewPoCosts: {
    totalCost: number;
    unitCost: number;
  };
  ticketGroupPreviews: Array<{
    ticketGroupId: number;
    eventDescription: string;
    quantity: number;
    currentUnitCost: number;
    newUnitCost: number;
  }>;
}

const MOCK_DELAY_MS = 200;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Mock preview API: computes new target-currency costs by applying
 * the ratio (newFxRate / currentFxRate) to all existing costs.
 */
export async function adjustFxRatePreview(
  req: AdjustFxRateRequest,
  purchaseOrder: PurchaseOrderDetails
): Promise<AdjustFxRatePreviewResponse> {
  await delay(MOCK_DELAY_MS);

  // conversionRate is the authoritative FX rate from POS-7760
  const currentFxRate = purchaseOrder.conversionRate ?? 1.0;
  const ratio = currentFxRate > 0 ? req.newFxRate / currentFxRate : 1.0;

  // originalCurrencyCode is the pre-conversion currency; currency is the
  // post-conversion (target/account) currency — same logic as the dialog.
  const originalCurrency =
    purchaseOrder.originalCurrencyCode ?? purchaseOrder.currency;
  const targetCurrency = purchaseOrder.currency;

  const currentTotalCost = purchaseOrder.totalCst?.amt ?? 0;
  const currentUnitCost = purchaseOrder.unitCst?.amt ?? 0;

  const ticketGroupPreviews = (purchaseOrder.ticketGroups ?? []).map((tg) => {
    const currentTgUnitCost = tg.unitCst?.amt ?? 0;
    const currentTgTotalCost = tg.totalCst?.amt ?? 0;
    const quantity = tg.tickets?.length ?? 1;

    return {
      ticketGroupId: tg.poId ?? 0,
      eventDescription: tg.vendOrdId ?? `Ticket Group`,
      quantity,
      currentUnitCost: currentTgUnitCost,
      newUnitCost: currentTgUnitCost * ratio,
    };
  });

  return {
    purchaseOrderId: req.purchaseOrderId,
    originalCurrency,
    targetCurrency,
    currentFxRate,
    newFxRate: req.newFxRate,
    currentPoCosts: {
      totalCost: currentTotalCost,
      unitCost: currentUnitCost,
    },
    previewPoCosts: {
      totalCost: currentTotalCost * ratio,
      unitCost: currentUnitCost * ratio,
    },
    ticketGroupPreviews,
  };
}

// adjustFxRate mock removed — the apply action now calls the real
// PurchaseClient.updatePurchaseOrderConversionRate endpoint.
